import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import { LabLocation } from '@team-milva/lab-models/labLocation'
import type { ILabLocation } from '@team-milva/lab-models/labLocation'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { orderBy } from 'lodash-es'
import { defineStore } from 'pinia'

type TUpdateLabLocationValues = {
  name?: string
  address?: ILabLocation['address']
  responsibleEmployeeID?: string | null
  canForwardSamples?: boolean
  activeState?: ILabLocation['activeState']
}

export const useLabLocationStore = defineStore('labLocation', {
  state: () => ({
    labLocations: [] as LabLocation[],
    unsubscribeLabLocations: null as (() => void) | null,
  }),

  actions: {
    getLabLocations(): Promise<LabLocation[]> {
      if (this.labLocationsLoaded) {
        return Promise.resolve(this.labLocations)
      }

      const accountStore = useAccountStore()
      if (!accountStore.labAccount?.id) {
        return Promise.resolve([])
      }

      return new Promise((resolve) => {
        let resolved = false

        const unsubscribe = onSnapshot(
          query(
            collection(db, 'Apps', '-lab-management-', 'LabLocations'),
            where('labAccountID', '==', accountStore.labAccount!.id)
          ),
          (snapshot) => {
            this.labLocations = orderBy(
              snapshot.docs.map(
                (doc) => new LabLocation(doc.data() as ILabLocation)
              ),
              [(labLocation) => labLocation.name.toLowerCase()],
              ['asc']
            )
            this.unsubscribeLabLocations = unsubscribe

            if (!resolved) {
              resolved = true
              resolve(this.labLocations)
            }
          }
        )
      })
    },

    async createLabLocation({
      labAccountID,
      data,
    }: {
      labAccountID: string
      data: {
        name: string
        address?: ILabLocation['address']
        responsibleEmployeeID?: string | null
        canForwardSamples?: boolean
      }
    }) {
      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/lab-locations',
        {
          path: { labAccountID },
          body: {
            name: data.name,
            address: data.address,
            responsibleEmployeeID: data.responsibleEmployeeID,
            canForwardSamples: data.canForwardSamples,
          },
        }
      )
    },

    async updateLabLocation(
      labLocationID: string,
      values: TUpdateLabLocationValues
    ) {
      const accountStore = useAccountStore()

      const body: Record<string, unknown> = {}
      if (values.name !== undefined) body['name'] = values.name
      if (values.address !== undefined) body['address'] = values.address
      if (values.responsibleEmployeeID !== undefined) {
        body['responsibleEmployeeID'] = values.responsibleEmployeeID
      }
      if (values.canForwardSamples !== undefined) {
        body['canForwardSamples'] = values.canForwardSamples
      }
      if (values.activeState !== undefined) {
        body['activeState'] = values.activeState
      }

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return await (managementApi as any)(
        'patch',
        'lab-accounts/{labAccountID}/lab-locations/{labLocationID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            labLocationID,
          },
          body,
        }
      )
    },

    async deactivateLabLocation(labLocationID: string): Promise<void> {
      await this.updateLabLocation(labLocationID, { activeState: 'inactive' })
    },

    async reactivateLabLocation(labLocationID: string): Promise<void> {
      await this.updateLabLocation(labLocationID, { activeState: 'active' })
    },

    resetStore(): void {
      this.labLocations = []
      if (this.unsubscribeLabLocations) {
        this.unsubscribeLabLocations()
        this.unsubscribeLabLocations = null
      }
    },
  },

  getters: {
    labLocationsLoaded(state): boolean {
      return state.unsubscribeLabLocations !== null
    },
    activeLabLocations(state): LabLocation[] {
      return state.labLocations.filter((x) => x.activeState === 'active')
    },
  },
})
